import React from "react";
import { useRootSelector } from "@/infrastructure/hooks";
import { FireType } from "@/features/firestore";
import { Button, List, notification, Popconfirm, Tag, Typography } from "antd";
import { oc } from "ts-optchain";
import { useDispatch } from "react-redux";
import { BacktestAction, BacktestType } from "@/features/backtest";
import { AppSel } from "@/features/applications";
import { firebaseDb } from "@/infrastructure/firebase";
import { DeleteOutlined, DownloadOutlined } from "@ant-design/icons/lib";
import { useSetupContext } from "@/modules/Backtest/hooks";

import styles from "../styles.less";

type Props = {
  templates: FireType.BacktestSnapshot[];
};

const { Text } = Typography;

export default ({ templates }: Props) => {
  const dispatch = useDispatch();
  const { form } = useSetupContext();
  const userId = useRootSelector(AppSel.userId);

  return (
    <List
      bordered
      itemLayout="vertical"
      pagination={{
        pageSizeOptions: ["5", "10", "20"],
        showSizeChanger: true,
        defaultPageSize: 5,
        position: "bottom"
      }}
      dataSource={oc(templates)([]).sort((a, b) => a.name.localeCompare(b.name))}
      renderItem={item => (
        <List.Item
          actions={[
            <Text key={"author"}>
              Author: <Tag color="blue">{item.author}</Tag>
            </Text>,
            <Text key={"date"}>
              Date: <Tag color="cyan">{item.date}</Tag>
            </Text>
          ]}
          extra={[
            <Button
              className={styles.extraBtn}
              key={"download"}
              icon={<DownloadOutlined />}
              shape={"round"}
              size={"small"}
              onClick={() => loadSnapshot(item.name, item.setup, item.results)}
            />,
            <Popconfirm
              key={"delete"}
              placement="left"
              title={`Are you sure to delete snapshot ${item.name}? This action cannot de undone.`}
              onConfirm={() => deleteSnapshot(item.name)}
              okText="Yes"
              okType={"danger"}
              cancelText="No"
            >
              <Button
                className={styles.extraBtn}
                danger
                icon={<DeleteOutlined />}
                shape={"round"}
                size={"small"}
                style={{ marginLeft: 10 }}
              />
            </Popconfirm>
          ]}
        >
          <List.Item.Meta title={item.name} description={item.description} />
        </List.Item>
      )}
    />
  );

  async function loadSnapshot(name: string, setup: BacktestType.Setup, result?: BacktestType.Result) {
    await dispatch(BacktestAction.loadSnapshots({ setup, result }));
    notification.success({
      message: `Snapshot: ${name} loaded successfully!`
    });
    // To reflect the snapshot setup
    form.resetFields();
  }

  async function deleteSnapshot(name: string) {
    await firebaseDb
      .collection("backtest_snapshot")
      .doc(`${userId}_${name}`)
      .delete();

    notification.success({
      message: `Snapshot: ${name} deleted successfully!`
    });
  }
};
